import React, { useCallback, useMemo, useContext, useState, useEffect } from 'react';
import { Callout, Stack, DefaultButton } from '@bingads-webui-react/fluent-controls';
import _, { noop } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useId } from '@fluentui/react-hooks';
import {
  AssetEventRecordType,
  AssetPropertyName,
  AssetType,
  useEventLog,
  POST_ASSET_EVENT_NAME,
  EMPTY_ASSET_HINTS,
  NOT_SUPPORT_DYNAMIC_TEXT,
  NO_FINAL_URL_HINT
} from '@bingads-webui-campaign-react/copilot-common';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UnvalidatedTextAssetCollection } from '@bingads-webui-campaign-react/validated-text-collection';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { CopilotTextAssetRecommendation } from '@bingads-webui/inline-copilot';
import { getCharCountExcludingAdCustomizers } from '@bingads-webui-campaign/ad-customizers-next';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { unlocalizedRecommendationTitle } from './recommendations-constants';
import { getTextAssetRecommendationsStyles } from './styles';
import { InlineRecommendations } from './inline-recommendations';
import { TextAssetRecommendationsDropdown } from './text-asset-recommendations-dropdown';
import { DKIThemeDefaultName } from './pmax-text-asset-collection-config';

const recommendationFieldMap = {
  Headlines: 'headlines',
  LongHeadlines: 'longHeadlines',
  Descriptions: 'descriptions',
};

const recommendationAssetTypeMap = {
  Headlines: 'headline',
  LongHeadlines: 'longHeadline',
  Descriptions: 'description',
};

const propertyNameSingularMap = {
  Headlines: AssetPropertyName.HEADLINE,
  LongHeadlines: AssetPropertyName.LONG_HEADLINE,
  Descriptions: AssetPropertyName.DESCRIPTION,
};

const textAssetCollectionStyleOverridesForInlineRecommendations = {
  AddAssetButton: {
    marginBottom: 'unset',
  },
};

export const UnvalidatedTextAssetCollectionWithRecommendations = React.memo((props) => {
  const {
    validationPropsField,
    validationProps,
    updateValidation,
    maxCollectionSize,
    inlineRecommendationsTitle,
    inlineRecommendationsHelpTopicKey,
    inlineRecommendationsHelpTopicAriaLabel,
    inlineRecommendationsDisabledItemTooltip,
    singleAssetValidationPropsField,
    i18n,
    permissions,
    withInlineRecommendations,
    withDeleteIconDisabled,
    setAddTextAssetCallback,
    copilotButtonLabel,
    onFocusTextAsset,
    onApplyRecommendation,
    enableCopilotRecommendations,
    onDeleteTextAsset,
    enableCustomizer,
    hideAddAllSuggestionsButton,
    showAssetLabelledBy,
    isAudienceAd,
    enableDKI,
    DKIThemeName,
    replaceMode,
  } = props;

  const isAmplifyEnabled = _.chain(permissions).result('IsAmplifyEnabled').value();
  const is40CharHeadlinePilotEnabled = _.chain(permissions).result('dynamic').result('AudienceCampaignExpandedHeadlineCharLimits').value();
  const isExpandedHeadlineCharLimitEnabled = (isAudienceAd && !isAmplifyEnabled && is40CharHeadlinePilotEnabled);
  const updatedValidationSchema = {
    ...validationProps,
    schema: {
      ...validationProps.schema,
      properties: {
        ...validationProps.schema.properties,
        Headlines: {
          ...validationProps.schema.properties.Headlines,
          schema: {
            ...validationProps.schema.properties.Headlines.schema,
            properties: Object.fromEntries(Object.entries(validationProps.schema.properties.Headlines.schema.properties).map(([key, property]) =>
              [
                key,
                { ...property, maxLength: 40 },
              ])),
          },
        },
      },
    },
  };

  const newValidationProps = isExpandedHeadlineCharLimitEnabled ? updatedValidationSchema : validationProps;

  const { data } = validationProps;
  const collection = _.result(data, validationPropsField, {});
  const [viewedCopilotCollectionObj, setViewedCopilotCollectionObj] = useState();

  const finalUrlForRecommendation = useMemo(() => _.result(data, 'FinalUrlForRecommendation', false), [data]);

  const classes = useDefaultStyles(getTextAssetRecommendationsStyles);
  const [visibleCalloutKey, setVisibleCalloutKey] = React.useState();
  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const { postAssetEventLog = noop, copilotTracking } = recommendationContext;

  const serverContext = useContext(ServerContext);
  const [isCopilotEnabled, setIsCopilotEnabled] = useState();
  useEffect(() => {
    if (enableCopilotRecommendations) {
      isCopilotRegionRestricted({
        feature: 'Recommendations',
        permissions,
        serverContext,
      }).then(isRestricted => setIsCopilotEnabled(!isRestricted));
    } else {
      setIsCopilotEnabled(false);
    }
  }, [enableCopilotRecommendations, permissions, serverContext]);

  const logCopilotSuggestions = useCallback((suggestionIds) => {
    if (copilotTracking) {
      const suggestionsToLog = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
      if (suggestionsToLog.length > 0) {
        postAssetEventLog({
          EventName: POST_ASSET_EVENT_NAME,
          EventValue: {
            AssetType: _.map(suggestionsToLog, () => AssetType.TEXT),
            SuggestionIds: suggestionsToLog,
            RecordType: AssetEventRecordType.VIEWED,
          },
        });
        copilotTracking.trackViewedSuggestionIds(suggestionsToLog);
      }
    }
  }, [copilotTracking, postAssetEventLog]);

  const {
    logTextAssetMenuOpen,
    logTextAssetDialogOpen,
    logRefineTextAsset,
    logRefineTextAssetSucceeded,
    logTextAssetApplied,
    logRemoveSingleAsset,
  } = useEventLog(postAssetEventLog);

  const onSuggestionsRendered = useCallback((finalUrl, params, textSuggestions) => {
    const textSuggestionIds = _.pluck(textSuggestions, 'RecommendationId');
    logRefineTextAssetSucceeded(finalUrl, params, textSuggestionIds);

    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets({ TextAssets: textSuggestions });
      copilotTracking.trackViewedSuggestionIds(textSuggestionIds);
    }
  }, [copilotTracking, logRefineTextAssetSucceeded]);

  const recommendationsField = recommendationFieldMap[validationPropsField];
  const collectionRecommendations = useMemo(() => {
    if (isCopilotEnabled) {
      const { themes } = recommendationContext;
      return _.map(themes, theme => ({
        name: theme.name,
        assets: _.result(theme, recommendationsField),
      }));
    }
    return _.result(recommendationContext, recommendationsField);
  }, [isCopilotEnabled, recommendationContext, recommendationsField]);

  const currentCollectionTexts = useMemo(
    () => _.chain(data).result(validationPropsField, {}).values().value(),
    [data, validationPropsField]
  );

  const availableRecommendations = useMemo(
    () => {
      if (isCopilotEnabled) {
        let availableCollectionRecommendations = collectionRecommendations.filter(theme => theme.name !== DKIThemeDefaultName);
        if (permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && enableDKI) {
          const DKItheme = collectionRecommendations.filter(theme => theme.name === DKIThemeDefaultName);
          availableCollectionRecommendations = [...DKItheme, ...availableCollectionRecommendations];
        }
        return _.chain(availableCollectionRecommendations)
          .map(theme => ({
            name: theme.name === DKIThemeDefaultName ? DKIThemeName : theme.name,
            assets: _.chain(theme.assets)
              .map(_.property('Text'))
              .filter(recommendation => !_.contains(currentCollectionTexts, recommendation))
              .value(),
          }))
          .filter(theme => !_.isEmpty(theme.assets))
          .value();
      }
      return _.chain(collectionRecommendations)
        .map(_.property('Text'))
        .filter(recommendation => !_.contains(currentCollectionTexts, recommendation))
        .value();
    },
    [DKIThemeName, collectionRecommendations, currentCollectionTexts, enableDKI, isCopilotEnabled, permissions]
  );

  const recommendationsMap = useMemo(() => {
    if (isCopilotEnabled) {
      return _.chain(collectionRecommendations)
        .map(theme => _.chain(theme.assets)
          .map(recommendation => [
            _.property('Text')(recommendation),
            {
              Text: _.property('Text')(recommendation),
              SuggestionId: _.property('RecommendationId')(recommendation),
            },
          ])
          .object()
          .value())
        .reduce((result, theme) => _.extend(result, theme), {})
        .value();
    }
    return _.chain(collectionRecommendations)
      .map(recommendation => [
        recommendation.Text,
        {
          ...recommendation,
          SuggestionId: recommendation.SuggestionId,
        },
      ])
      .object()
      .value();
  }, [collectionRecommendations, isCopilotEnabled]);

  const reportDisplayedRecommendations = useCallback((recommendations) => {
    const displayedRecommendations = _.chain(recommendations)
      .map(recommendation => recommendationsMap[recommendation])
      .compact()
      .value();

    if (!_.isEmpty(displayedRecommendations)) {
      const displayedRecommendationsIds = _.pluck(displayedRecommendations, 'SuggestionId');
      reportRecommendations({
        view: displayedRecommendationsIds,
      });
      if (isCopilotEnabled) {
        logCopilotSuggestions(displayedRecommendationsIds);
      }
    }
  }, [logCopilotSuggestions, isCopilotEnabled, recommendationsMap, reportRecommendations]);

  useEffect(() => {
    if (viewedCopilotCollectionObj && recommendationContext) {
      reportDisplayedRecommendations(viewedCopilotCollectionObj);
    }
  }, [recommendationContext, reportDisplayedRecommendations, viewedCopilotCollectionObj]);

  const applyRecommendation = useCallback((key, text) => {
    const collectionData = cloneDeep(collection);
    collectionData[key] = text;
    const newData = {
      ...data,
      [validationPropsField]: collectionData,
    };

    setVisibleCalloutKey(null);
    updateValidation(null, newData);

    onApplyRecommendation(key);
  }, [collection, data, onApplyRecommendation, updateValidation, validationPropsField]);

  const applyRecommendations = useCallback((recommendations, replaceExistingAssets) => {
    const availableRecommendationsStack = [...recommendations].reverse();
    const collectionObj = _.result(data, validationPropsField, {});
    const fieldsToSet = Math.max(maxCollectionSize, _.size(collectionObj));

    // Remove all existing assets if replaceExistingAssets is true
    if (replaceExistingAssets) {
      for (let currentFieldIndex = 0; currentFieldIndex < fieldsToSet; currentFieldIndex += 1) {
        delete collectionObj[`${singleAssetValidationPropsField}${currentFieldIndex}`];
      }
    }

    for (let currentFieldIndex = 0; currentFieldIndex < fieldsToSet && !_.isEmpty(availableRecommendationsStack); currentFieldIndex += 1) {
      const key = `${singleAssetValidationPropsField}${currentFieldIndex}`;
      if (_.isEmpty(collectionObj[key])) {
        const recommendation = availableRecommendationsStack.pop();
        collectionObj[key] = recommendation;
      }
    }

    const newData = {
      [validationPropsField]: collectionObj,
    };
    updateValidation([], newData);
    if (isCopilotEnabled) {
      setViewedCopilotCollectionObj(collectionObj);
    }
  }, [data, maxCollectionSize, isCopilotEnabled, singleAssetValidationPropsField, updateValidation, validationPropsField]);

  if (_.isFunction(setAddTextAssetCallback)) {
    setAddTextAssetCallback((recommendations, replaceExistingAssets) => {
      const filteredRecommendations = !replaceExistingAssets
        ? recommendations.filter(recommendation => !_.contains(currentCollectionTexts, recommendation))
        : recommendations;

      applyRecommendations(filteredRecommendations, replaceExistingAssets);
    });
  }

  const headlineTitle = useMemo(() => i18n.getString(unlocalizedRecommendationTitle[validationPropsField]), [i18n, validationPropsField]);

  const renderRecommendations = useCallback((key, itemValue) => {
    const filteredRecommendations = isCopilotEnabled
      ? _.chain(availableRecommendations)
        .map((theme) => {
          if (theme.name === DKIThemeName) {
            return {
              ...theme,
              assets: _.filter(theme.assets, recommendation => recommendation.toLowerCase().startsWith(itemValue.toLowerCase())).slice(0, 2),
            };
          }
          return {
            ...theme,
            assets: _.filter(theme.assets, recommendation => recommendation.toLowerCase().startsWith(itemValue.toLowerCase())),
          };
        })
        .filter(theme => !_.isEmpty(theme.assets))
        .value()
      : _.filter(availableRecommendations, recommendation => recommendation.toLowerCase().startsWith(itemValue.toLowerCase()));

    const displayedRecommendations = isCopilotEnabled
      ? _.chain(availableRecommendations)
        .map(theme => _.filter(theme.assets, recommendation => recommendation.toLowerCase().startsWith(itemValue.toLowerCase())))
        .flatten()
        .value()
      : filteredRecommendations;

    if (_.isEmpty(filteredRecommendations)) {
      return null;
    }

    reportDisplayedRecommendations(displayedRecommendations);

    return isCopilotEnabled ? (
      <TextAssetRecommendationsDropdown
        assetKey={key}
        applyRecommendation={applyRecommendation}
        recommendations={filteredRecommendations}
        i18n={i18n}
        DKIThemeName={DKIThemeName}
      />
    ) : (
      <div className={classnames('text-asset-recommendation-content', classes.TextAssetRecommendationsContent)}>
        <Stack>
          <span className="text-recommendation-title">{headlineTitle}</span>
          {filteredRecommendations.map(recommendation => <DefaultButton onClick={() => applyRecommendation(key, recommendation)}>{recommendation}</DefaultButton>)}
        </Stack>
      </div>
    );
  }, [isCopilotEnabled, availableRecommendations, reportDisplayedRecommendations, classes.TextAssetRecommendationsContent, headlineTitle, applyRecommendation]);

  const { Headlines, LongHeadlines, Descriptions } = data || {};
  const existingAssets = useMemo(() => ({
    shortHeadlines: _.values(Headlines),
    longHeadlines: _.values(LongHeadlines),
    descriptions: _.values(Descriptions),
  }), [Headlines, LongHeadlines, Descriptions]);

  const handleDeleteTextAsset = useCallback((key, deletedValue) => {
    onDeleteTextAsset(key);

    const propertyName = propertyNameSingularMap[validationPropsField];
    if (!isCopilotEnabled || !copilotTracking || !copilotTracking.isInRemainingInitialSuggestions(propertyName, deletedValue)) { return; }

    copilotTracking.removeInitialSuggestion(propertyNameSingularMap[validationPropsField], deletedValue);

    const recommendationId = _.chain(collectionRecommendations)
      .map('assets')
      .flatten()
      .filter(recommendation => recommendation.Text === deletedValue)
      .map('RecommendationId')
      .first()
      .value();

    if (recommendationId) {
      logRemoveSingleAsset(propertyName, recommendationId);
    }
  }, [onDeleteTextAsset, isCopilotEnabled, collectionRecommendations, validationPropsField, logRemoveSingleAsset, copilotTracking]);

  const renderInlineCopilot = useCallback((key, itemValue) => {
    const assetType = recommendationAssetTypeMap[validationPropsField];

    let disableReason;
    if (!finalUrlForRecommendation) {
      disableReason = i18n.getString(NO_FINAL_URL_HINT);
    }
    if (_.isEmpty(itemValue.trim())) {
      disableReason = i18n.getString(EMPTY_ASSET_HINTS[assetType]);
    }

    if (enableCustomizer) {
      if (getCharCountExcludingAdCustomizers({
        text: itemValue,
        adType: 'ResponsiveAd',
        field: validationPropsField,
      }) !== itemValue.length) {
        disableReason = i18n.getString(NOT_SUPPORT_DYNAMIC_TEXT);
      }
    }

    const currentAssetIndex = parseInt(key.match(/\d+/)[0], 10);

    const onSelect = (assets, operationName) => {
      const text = _.get(assets, ['value', 0, 'text']);
      logTextAssetApplied(assetType, operationName);
      return applyRecommendation(key, text);
    };

    return (
      <CopilotTextAssetRecommendation
        assetType={assetType}
        currentIndex={currentAssetIndex}
        existingAssets={existingAssets}
        disableReason={disableReason}
        menuButtonAriaLabel={copilotButtonLabel}
        onMenuOpen={logTextAssetMenuOpen}
        onOperationDialogOpen={logTextAssetDialogOpen}
        onFetch={logRefineTextAsset}
        onSelect={onSelect}
        onSuggestionsRendered={onSuggestionsRendered}
        isNewLogo={_.get(permissions, ['dynamic', 'AdsCopilotUxImprovements'])}
      />
    );
  }, [
    applyRecommendation,
    copilotButtonLabel,
    existingAssets,
    finalUrlForRecommendation,
    i18n,
    logRefineTextAsset,
    logTextAssetApplied,
    logTextAssetDialogOpen,
    logTextAssetMenuOpen,
    onSuggestionsRendered,
    validationPropsField,
    enableCustomizer,
    permissions,
  ]);

  const labelledById = useId('textAssetLabel');

  const renderFunction = useCallback((key, renderItemFunction, itemValue) => {
    const onClickOrChangeHandler = () => setVisibleCalloutKey(availableRecommendations.length > 0 && key);

    const additionalTextFieldProps = {
      id: key,
      onClick: onClickOrChangeHandler,
      onChange: onClickOrChangeHandler,
      renderInlineCopilot: isCopilotEnabled ? renderInlineCopilot : null,
      onFocus: () => onFocusTextAsset(key),
    };

    if (isCopilotEnabled && availableRecommendations.length > 0) {
      // when inline recommendations are available, hide the browser's native autocomplete
      additionalTextFieldProps.autoComplete = 'off';
    }

    if (showAssetLabelledBy) {
      additionalTextFieldProps['aria-labelledBy'] = labelledById;
    }

    return (
      <React.Fragment>
        {renderItemFunction(additionalTextFieldProps)}
        {visibleCalloutKey === key &&
          <Callout
            target={`#${key}`}
            isBeakVisible={false}
            gapSpace={0}
            role="dialog"
            onDismiss={() => setVisibleCalloutKey(undefined)}
            calloutMinWidth={435}
          >
            {renderRecommendations(key, itemValue)}
          </Callout>
        }
      </React.Fragment>);
  }, [availableRecommendations.length, isCopilotEnabled, labelledById, onFocusTextAsset, renderInlineCopilot, renderRecommendations, showAssetLabelledBy, visibleCalloutKey]);

  return (
    <FluentProvider theme={webLightTheme}>
      <UnvalidatedTextAssetCollection
        {...{ ...props, validationProps: newValidationProps }}
        onDeleteTextAsset={handleDeleteTextAsset}
        renderWrapperFunction={renderFunction}
        withDeleteIconDisabled={withDeleteIconDisabled}
        labelId={labelledById}
        {...withInlineRecommendations ? { styleOverrides: textAssetCollectionStyleOverridesForInlineRecommendations } : {}}
      />
      {withInlineRecommendations &&
        <InlineRecommendations
          i18n={i18n}
          collection={collection}
          collectionRecommendations={collectionRecommendations}
          maxCollectionSize={maxCollectionSize}
          availableRecommendations={availableRecommendations}
          singleAssetValidationPropsField={singleAssetValidationPropsField}
          inlineRecommendationsHelpTopicKey={inlineRecommendationsHelpTopicKey}
          inlineRecommendationsHelpTopicAriaLabel={inlineRecommendationsHelpTopicAriaLabel}
          inlineRecommendationsTitle={inlineRecommendationsTitle}
          inlineRecommendationsDisabledItemTooltip={inlineRecommendationsDisabledItemTooltip}
          applyRecommendation={applyRecommendation}
          applyRecommendations={applyRecommendations}
          reportDisplayedRecommendations={reportDisplayedRecommendations}
          permissions={permissions}
          enableCopilotRecommendations={isCopilotEnabled}
          hideAddAllSuggestionsButton={hideAddAllSuggestionsButton}
          enableDKI={permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && enableDKI}
          DKIThemeName={DKIThemeName}
          replaceMode={replaceMode}
        />
      }
    </FluentProvider>
  );
});

UnvalidatedTextAssetCollectionWithRecommendations.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  campaignId: PropTypes.number.isRequired,
  validationPropsField: PropTypes.string.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    getLocale: PropTypes.func.isRequired,
  }).isRequired,
  singleAssetValidationPropsField: PropTypes.string.isRequired,
  maxCollectionSize: PropTypes.number.isRequired,
  inlineRecommendationsTitle: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicKey: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicAriaLabel: PropTypes.string.isRequired,
  inlineRecommendationsDisabledItemTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  withInlineRecommendations: PropTypes.bool,
  withDeleteIconDisabled: PropTypes.bool,
  setAddTextAssetCallback: PropTypes.func,
  copilotButtonLabel: PropTypes.string,
  onFocusTextAsset: PropTypes.func,
  onApplyRecommendation: PropTypes.func,
  enableCopilotRecommendations: PropTypes.bool,
  onDeleteTextAsset: PropTypes.func,
  enableCustomizer: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      isAmplifyEnabled: PropTypes.bool,
      AudienceCampaignExpandedHeadlineCharLimits: PropTypes.bool,
    }),
  }).isRequired,
  hideAddAllSuggestionsButton: PropTypes.bool,
  showAssetLabelledBy: PropTypes.bool,
  isAudienceAd: PropTypes.bool,
  enableDKI: PropTypes.bool,
  DKIThemeName: PropTypes.string,
  replaceMode: PropTypes.bool,
};

UnvalidatedTextAssetCollectionWithRecommendations.defaultProps = {
  withInlineRecommendations: false,
  withDeleteIconDisabled: false,
  setAddTextAssetCallback: noop,
  copilotButtonLabel: undefined,
  onFocusTextAsset: noop,
  onApplyRecommendation: noop,
  enableCopilotRecommendations: true,
  onDeleteTextAsset: noop,
  enableCustomizer: false,
  hideAddAllSuggestionsButton: false,
  showAssetLabelledBy: false,
  isAudienceAd: false,
  enableDKI: false,
  DKIThemeName: '',
  replaceMode: false,
};

export const TextAssetCollectionWithRecommendations = withPermissions(withCustomValidation(UnvalidatedTextAssetCollectionWithRecommendations));
